import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Input, Select, Textarea, Chip } from "../components/ui";
import {
  ArrowLeft, Building2, MapPin, Globe, Clock, DollarSign, QrCode,
  Hash, Save, Shield, Users, Crown, Trash2, RotateCcw, Upload, Image, X
} from "lucide-react";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
};

export default function ClubSettingsPage() {
  const nav = useNavigate();
  const { club, user, refreshMe } = useAuth();
  const { clubId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferMemberId, setTransferMemberId] = useState("");
  const [members, setMembers] = useState([]);
  const [clubData, setClubData] = useState({
    name: "",
    logo: "",
    description: "",
    city: "",
    country: "",
    timezone: "Asia/Kolkata",
    currency: "INR",
    default_match_fee: 0,
    upi_id: "",
  });

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const base64 = await fileToBase64(file);
      setClubData(prev => ({ ...prev, logo: base64 }));
    } catch (err) {
      console.error('Failed to convert image:', err);
      alert('Failed to process image');
    } finally {
      setUploadingLogo(false);
    }
  };

  const timezones = [
    "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo",
    "Australia/Sydney", "Australia/Melbourne", "Pacific/Auckland",
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "America/Toronto", "America/Vancouver",
    "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
    "Africa/Johannesburg", "Africa/Cairo",
    "UTC"
  ];

  const countries = [
    "India", "Australia", "England", "South Africa", "New Zealand", "Pakistan", "Sri Lanka",
    "Bangladesh", "West Indies", "Afghanistan", "Ireland", "Zimbabwe", "Netherlands",
    "United Arab Emirates", "Oman", "Scotland", "USA", "Canada", "Singapore", "Hong Kong"
  ];

  const fetchData = useCallback(async () => {
    try {
      const [{ data: clubDetails }, { data: membersList }] = await Promise.all([
        api.get(`/clubs/${clubId}/settings`),
        api.get("/members"),
      ]);
      setClubData(clubDetails);
      setMembers(membersList.filter(m => m.active && m.user_id && m.user_id !== user.id));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [clubId, user.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClubData(prev => ({ ...prev, [name]: name === "default_match_fee" ? parseFloat(value) || 0 : value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/clubs/${clubId}/settings`, clubData);
      toast.success("Settings saved!");
      await refreshMe();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferMemberId) return;
    try {
      await api.post(`/clubs/${clubId}/transfer-ownership`, { new_owner_id: transferMemberId });
      toast.success("Ownership transferred!");
      setShowTransferModal(false);
      await refreshMe();
      nav("/home", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleDelete = async () => {
    try {
      await api.post(`/clubs/${clubId}/archive`);
      toast.success("Club archived");
      await refreshMe();
      nav("/home", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const isOwner = club?.my_role === "owner";

  if (loading) {
    return (
      <div className="app-shell flex flex-col min-h-screen bg-white">
        <div className="flex-1 grid place-items-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="app-shell flex flex-col min-h-screen bg-white">
        <div className="px-5 pt-8 pb-4">
          <h1 className="text-2xl font-bold">Club Settings</h1>
        </div>
        <div className="px-5 flex-1 grid place-items-center">
          <Card className="text-center max-w-md">
            <Shield size={48} className="mx-auto text-ink-muted/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Owner only</h3>
            <p className="text-ink-muted">Only the club owner can access settings.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell flex flex-col min-h-screen bg-white">
      <div className="px-5 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Club Settings</h1>
            <p className="text-ink-muted text-sm">{club?.name}</p>
          </div>
        </div>
      </div>

      <div className="px-5 flex-1 overflow-y-auto pb-10">
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6 max-w-2xl mx-auto">
          <Card>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Hash size={18} className="text-brand" /> Basic Details
            </h3>
            <div className="space-y-4">
              <Input
                label="Club Name *"
                placeholder="Sunday Warriors CC"
                name="name"
                value={clubData.name}
                onChange={handleChange}
                required
                data-testid="club-name-input"
              />
              <div className="space-y-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-ink-muted font-semibold block mb-1.5">
                    Club Logo
                  </span>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                        data-testid="club-logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="cursor-pointer"
                      >
                        {clubData.logo && clubData.logo.startsWith('data:') ? (
                          <img src={clubData.logo} alt="Club logo" className="h-20 w-20 rounded-xl object-cover border border-black/10" />
                        ) : clubData.logo ? (
                          <img src={clubData.logo} alt="Club logo" className="h-20 w-20 rounded-xl object-cover border border-black/10" />
                        ) : (
                          <div className="h-20 w-20 rounded-xl bg-brand-100 grid place-items-center border-2 border-dashed border-brand-300">
                            <Upload size={28} className="text-brand" />
                          </div>
                        )}
                      </label>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('logo-upload').click()}
                        disabled={uploadingLogo}
                        data-testid="club-logo-upload-btn"
                      >
                        <Upload size={16} /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
                      </Button>
                      {clubData.logo && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setClubData(prev => ({ ...prev, logo: "" }))}
                          className="text-red-500 hover:bg-red-50"
                          data-testid="club-logo-remove-btn"
                        >
                          <X size={14} /> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </label>
              </div>
              <Textarea
                label="Description"
                placeholder="A friendly cricket club for weekend matches..."
                name="description"
                value={clubData.description}
                onChange={handleChange}
                rows={3}
                data-testid="club-description-input"
              />
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <MapPin size={18} className="text-brand" /> Location
            </h3>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="City"
                  placeholder="Mumbai"
                  name="city"
                  value={clubData.city}
                  onChange={handleChange}
                  data-testid="club-city-input"
                />
                <Select
                  label="Country"
                  name="country"
                  value={clubData.country}
                  onChange={handleChange}
                  data-testid="club-country-select"
                >
                  {countries.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </div>
              <Select
                label="Timezone"
                name="timezone"
                value={clubData.timezone}
                onChange={handleChange}
                data-testid="club-timezone-select"
              >
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>{tz.replace("_", " ")}</option>
                ))}
              </Select>
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <DollarSign size={18} className="text-brand" /> Currency & Payments
            </h3>
            <div className="space-y-4">
              <Select
                label="Currency"
                name="currency"
                value={clubData.currency}
                onChange={handleChange}
                data-testid="club-currency-select"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="AED">AED (د.إ)</option>
                <option value="SGD">SGD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="EUR">EUR (€)</option>
              </Select>
              <Input
                label="Default Match Fee"
                placeholder="500"
                type="number"
                step="0.01"
                min="0"
                name="default_match_fee"
                value={clubData.default_match_fee}
                onChange={handleChange}
                data-testid="club-match-fee-input"
              />
              <Input
                label="Club UPI ID"
                placeholder="club@upi"
                name="upi_id"
                value={clubData.upi_id}
                onChange={handleChange}
                data-testid="club-upi-input"
              />
            </div>
          </Card>

          <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={saving} data-testid="save-settings-btn">
            <Save size={18} />
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </form>

        {/* Danger Zone */}
        <Card className="mt-8 border-red-200 bg-red-50">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-red-600">
            <Trash2 size={18} /> Danger Zone
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white rounded-2xl border border-red-100">
              <div>
                <div className="font-medium text-red-700">Transfer Ownership</div>
                <div className="text-sm text-red-600">Make another member the owner of this club. You will become an admin.</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowTransferModal(true)} data-testid="transfer-ownership-btn">
                <RotateCcw size={16} /> Transfer
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 bg-white rounded-2xl border border-red-100">
              <div>
                <div className="font-medium text-red-700">Archive Club</div>
                <div className="text-sm text-red-600">Permanently archive this club. All data will be preserved but the club will be hidden.</div>
              </div>
              <Button variant="danger" size="sm" onClick={() => setShowDeleteModal(true)} data-testid="archive-club-btn">
                <Trash2 size={16} /> Archive
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowTransferModal(false)}>
          <Card className="w-full max-w-md animate-in slide-in-from-bottom-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Transfer Ownership</h3>
              <button onClick={() => setShowTransferModal(false)} className="h-9 w-9 rounded-full bg-black/5 hover:bg-black/10">×</button>
            </div>
            <p className="text-ink-muted text-sm mb-4">Select a member to become the new owner. You will become an admin.</p>
            <select
              value={transferMemberId}
              onChange={(e) => setTransferMemberId(e.target.value)}
              className="w-full h-12 px-4 rounded-2xl bg-white border border-black/10 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-ink mb-4"
              required
            >
              <option value="">Select member</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
              ))}
            </select>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowTransferModal(false)}>Cancel</Button>
              <Button variant="danger" className="flex-1" onClick={handleTransfer} disabled={!transferMemberId}>
                Transfer Ownership
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Archive Club Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowDeleteModal(false)}>
          <Card className="w-full max-w-md animate-in slide-in-from-bottom-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Archive Club</h3>
              <button onClick={() => setShowDeleteModal(false)} className="h-9 w-9 rounded-full bg-black/5 hover:bg-black/10">×</button>
            </div>
            <p className="text-ink-muted text-sm mb-4">This will archive "{clubData.name}". All data will be preserved but the club will be hidden. This action cannot be undone easily.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
              <Button variant="danger" className="flex-1" onClick={handleDelete}>
                <Trash2 size={16} /> Archive Club
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}